import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

export interface Toast {
  id?: string;
  title: string;
  message?: string;
  type: ToastType;
  duration?: number; // ms, 0 = persistent
  dismissible?: boolean;
  timestamp: Date;
}

/**
 * Toast Notification Service
 * 
 * Показує вспливаючі сповіщення користувачу
 * Використовується для помилок, успіху, попереджень
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastSubject = new Subject<Toast>();
  public toast$ = this.toastSubject.asObservable();

  private removeToastSubject = new Subject<string>();
  public removeToast$ = this.removeToastSubject.asObservable();

  constructor() {}

  /**
   * Show success toast
   */
  success(title: string, message?: string, duration: number = 5000): void {
    this.show({ 
      title, 
      message, 
      type: ToastType.SUCCESS, 
      duration, 
      dismissible: true,
      timestamp: new Date()
    });
  }

  /**
   * Show error toast
   */
  error(title: string, message?: string, duration: number = 0): void {
    this.show({ 
      title, 
      message, 
      type: ToastType.ERROR, 
      duration, 
      dismissible: true,
      timestamp: new Date()
    });
  }

  /**
   * Show warning toast
   */
  warning(title: string, message?: string, duration: number = 5000): void {
    this.show({ 
      title, 
      message, 
      type: ToastType.WARNING, 
      duration, 
      dismissible: true,
      timestamp: new Date()
    });
  }

  /**
   * Show info toast
   */
  info(title: string, message?: string, duration: number = 5000): void {
    this.show({ 
      title, 
      message, 
      type: ToastType.INFO, 
      duration, 
      dismissible: true,
      timestamp: new Date()
    });
  }

  /**
   * Show custom toast
   */
  show(toast: Toast): void {
    const id = toast.id || this.generateId();
    const toastWithId = { ...toast, id, timestamp: new Date() };
    
    this.toastSubject.next(toastWithId);

    // Auto-dismiss if duration > 0
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        this.dismiss(id);
      }, toast.duration);
    }
  }

  /**
   * Dismiss specific toast
   */
  dismiss(id: string): void {
    this.removeToastSubject.next(id);
  }

  /**
   * Clear all toasts
   */
  clearAll(): void {
    this.removeToastSubject.next('clear-all');
  }

  /**
   * Generate unique ID for toast
   */
  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
